import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Package, Clock, Truck, CheckCircle2, Loader2, Search, User, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../hooks/useAuth';
import { Combobox } from '../ui/Combobox';
import { CalendarSlotPicker } from '../booking/CalendarSlotPicker';
import useSWR from 'swr';
import { validatePhone } from '../../utils/fieldValidation';
import { formatInWarehouseTimeZone, formatLocalWallClock } from '../../utils/timezoneDisplay';


interface ScheduleBookingSlideOverProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

type WizardStep = 1 | 2 | 3;

const STEP_META: Record<WizardStep, { label: string; icon: typeof Package }> = {
    1: { label: 'Customer & PO', icon: Package },
    2: { label: 'Date & Time', icon: Clock },
    3: { label: 'Carrier Details', icon: Truck },
};

// SWR fetcher for carriers list
const carrierFetcher = async ([url, token]: [string, string]) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return { carriers: [] };
    return res.json();
};

export function ScheduleBookingSlideOver({ open, onClose, onSuccess }: ScheduleBookingSlideOverProps) {
    const { user, token } = useAuthStore();
    const [step, setStep] = useState<WizardStep>(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [phoneError, setPhoneError] = useState('');

    // Data
    const [customers, setCustomers] = useState<any[]>([]);
    const [pos, setPos] = useState<any[]>([]);
    const [poLoading, setPoLoading] = useState(false);

    // Carrier autocomplete — fetch existing carriers for this warehouse
    const { data: carrierData } = useSWR<{ carriers: any[] }>(
        open && token ? ['/api/carriers', token] : null,
        carrierFetcher
    );
    const knownCarriers = carrierData?.carriers || [];

    // Form
    const [formData, setFormData] = useState({
        warehouseId: user?.warehouseId || '',
        startAt: '',
        endAt: '',
        startUtc: '',
        endUtc: '',
        customerPoNumber: '',
        carrierName: '',
        carrierMcNumber: '',
        carrierEmail: '',
        carrierPhone: '',
        truckNumber: '',
        trailerNumber: '',
        trailerType: '',
        customerId: '',
        estimatedPalletCount: 0,
        totalWeight: 0,
        notes: '',
        selectedPOs: [] as { id: string; poNumber: string; clientName: string; pallets: number; weight: number }[],
    });

    // Toggle body class to hide AI copilot widget
    useEffect(() => {
        if (open) {
            document.body.classList.add('booking-slideover-open');
        } else {
            document.body.classList.remove('booking-slideover-open');
        }
        return () => document.body.classList.remove('booking-slideover-open');
    }, [open]);

    // Reset on open
    useEffect(() => {
        if (open) {
            setStep(1);
            setFormData({
                warehouseId: user?.warehouseId || '',
                startAt: '',
                endAt: '',
                startUtc: '',
                endUtc: '',
                customerPoNumber: '',
                carrierName: '',
                carrierMcNumber: '',
                carrierEmail: '',
                carrierPhone: '',
                truckNumber: '',
                trailerNumber: '',
                trailerType: '',
                customerId: '',
                estimatedPalletCount: 0,
                totalWeight: 0,
                notes: '',
                selectedPOs: [],
            });
            setIsSubmitting(false);
            setPhoneError('');
        }
    }, [open, user?.warehouseId]);

    // Load customers
    useEffect(() => {
        if (open && formData.warehouseId) {
            api.getCustomers(formData.warehouseId)
                .then(res => setCustomers(res.customers || []))
                .catch(() => {});
        }
    }, [open, formData.warehouseId]);

    // Search POs
    const searchPOs = useCallback(async (query: string, customerId?: string) => {
        if (!formData.warehouseId) return;
        setPoLoading(true);
        try {
            const res = await api.searchWarehousePOs(query, customerId, true); // true = excludeClosed
            setPos(res.results || []);
        } catch { /* noop */ } finally {
            setPoLoading(false);
        }
    }, [formData.warehouseId]);

    // Initial PO load
    useEffect(() => {
        if (open && formData.warehouseId) searchPOs('', '');
    }, [open, formData.warehouseId, searchPOs]);

    const handlePOSelect = (poId: string) => {
        const selectedPO = pos.find(p => p.id === poId);
        if (!selectedPO) return;
        if (formData.selectedPOs.some(p => p.id === selectedPO.id)) {
            toast.error('PO already added', { icon: '⚠️' });
            return;
        }
        const newPO = {
            id: selectedPO.id,
            poNumber: selectedPO.customerPoNumber,
            clientName: selectedPO.customerName || selectedPO.clientName || 'Unknown',
            pallets: selectedPO.totalSkids || selectedPO.palletCount || 0,
            weight: selectedPO.totalWeight || 0,
        };
        const updated = [...formData.selectedPOs, newPO];
        const totalPallets = updated.reduce((s, p) => s + p.pallets, 0);
        const totalWeight = updated.reduce((s, p) => s + p.weight, 0);
        setFormData(prev => ({
            ...prev,
            selectedPOs: updated,
            customerPoNumber: updated[0]?.poNumber || '',
            estimatedPalletCount: totalPallets,
            totalWeight,
        }));
        toast.success(`PO ${selectedPO.customerPoNumber} added`, { icon: '🚀' });
    };

    const handlePORemove = (poId: string) => {
        const updated = formData.selectedPOs.filter(p => p.id !== poId);
        const totalPallets = updated.reduce((s, p) => s + p.pallets, 0);
        const totalWeight = updated.reduce((s, p) => s + p.weight, 0);
        setFormData(prev => ({
            ...prev,
            selectedPOs: updated,
            customerPoNumber: updated[0]?.poNumber || '',
            estimatedPalletCount: totalPallets,
            totalWeight,
        }));
    };

    const handleSlotSelect = (start: string, end: string, startUtc?: string, endUtc?: string) => {
        setFormData(prev => ({ ...prev, startAt: start, endAt: end, startUtc: startUtc || '', endUtc: endUtc || '' }));
        toast.success('Time slot selected!', { icon: '✨', position: 'bottom-center' });
    };

    const handleChange = (e: any) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value) || 0 : value,
        }));
    };

    // ── Carrier autocomplete: select known carrier → autofill ──
    const handleCarrierSelect = (carrierId: string) => {
        const c = knownCarriers.find((k: any) => k.id === carrierId);
        if (!c) return;
        setFormData(prev => ({
            ...prev,
            carrierName: c.name || c.legalName || prev.carrierName,
            carrierMcNumber: c.mcNumber || prev.carrierMcNumber,
            carrierEmail: c.email || prev.carrierEmail,
            carrierPhone: c.phone || prev.carrierPhone,
        }));
        toast.success(`Carrier "${c.name || c.legalName}" loaded`, { icon: '🚛' });
    };



    // ── CRITICAL: Only called from Step 3 final button ──
    const doSubmit = async () => {
        // Hard validation for all required carrier fields
        const missing: string[] = [];
        if (!formData.carrierName.trim()) missing.push('Carrier Name');
        if (!formData.carrierMcNumber.trim()) missing.push('MC Number');
        if (!formData.carrierEmail.trim()) missing.push('Carrier Email');
        if (!formData.carrierPhone.trim()) missing.push('Phone');
        if (!formData.startAt || !formData.endAt) missing.push('Date & Time');
        if (!formData.warehouseId) missing.push('Facility');

        if (missing.length > 0) {
            toast.error(`Missing required fields: ${missing.join(', ')}`);
            return;
        }

        // Phone format validation
        const pErr = validatePhone(formData.carrierPhone);
        if (pErr) {
            setPhoneError(pErr);
            toast.error(pErr);
            return;
        }
        setPhoneError('');

        setIsSubmitting(true);
        try {
            const { selectedPOs, startAt, endAt, startUtc, endUtc, ...rest } = formData;
            
            // Convert startAt ("YYYY-MM-DDTHH:mm") into scheduledDate and scheduledTime
            const [scheduledDate, scheduledTime] = startAt ? startAt.split('T') : ['', ''];
            const [scheduledEndDate, scheduledEndTime] = endAt ? endAt.split('T') : ['', ''];

            const createRes: any = await api.createAppointment({
                ...rest,
                poIds: selectedPOs.map(p => p.id),
                scheduledDate,
                scheduledTime,
                scheduledUtc: startUtc || undefined,
                scheduledEndDate,
                scheduledEndUtc: endUtc || undefined,
                scheduledEndTime,
                estimatedPalletCount: formData.estimatedPalletCount || undefined,
                carrierName: formData.carrierName,
                carrierMcNumber: formData.carrierMcNumber,
                carrierEmail: formData.carrierEmail,
                carrierPhone: formData.carrierPhone,
                truckNumber: formData.truckNumber || undefined,
                trailerNumber: formData.trailerNumber || undefined,
                trailerType: formData.trailerType || undefined,
            });
            const assignedDock = createRes?.assignedDockNumber || createRes?.appointment?.dockNumber;
            toast.success(assignedDock ? `Appointment scheduled — Assigned to Dock ${assignedDock}` : 'Appointment scheduled — Now on dock board and calendar', { duration: 5000, icon: '🎉' });
            onSuccess?.();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to create appointment');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step validation
    const canGoToStep3 = !!formData.startAt && !!formData.endAt;
    const carrierValid =
        !!formData.carrierName.trim() &&
        !!formData.carrierMcNumber.trim() &&
        !!formData.carrierEmail.trim() &&
        !!formData.carrierPhone.trim() &&
        !validatePhone(formData.carrierPhone);
    const canSubmit = canGoToStep3 && carrierValid;

    if (!open) return null;

    return (
        <div className="w-[420px] shrink-0 flex flex-col bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-xl h-full animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Schedule Appointment</h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Book a new dock appointment</p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Step indicator */}
            <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800/50">
                <div className="flex items-center gap-1">
                    {([1, 2, 3] as WizardStep[]).map((s) => {
                        const Icon = STEP_META[s].icon;
                        const isActive = s === step;
                        const isComplete = s < step;
                        return (
                            <div key={s} className="flex items-center gap-1 flex-1">
                                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors w-full justify-center ${
                                    isActive
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50'
                                        : isComplete
                                            ? 'bg-emerald-50 dark:bg-emerald-900/15 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40'
                                            : 'bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 border border-transparent'
                                }`}>
                                    {isComplete ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                                    <span className="hidden sm:inline">{STEP_META[s].label}</span>
                                    <span className="sm:hidden">{s}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Content — NO <form> element */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
                {/* Step 1: Customer & PO */}
                {step === 1 && (
                    <div className="space-y-5 animate-fade-in">
                        <div className="flex items-center gap-2 mb-4">
                            <Package className="w-5 h-5 text-blue-500" />
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Customer & PO</h3>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                Customer
                            </label>
                            <Combobox
                                placeholder="Select Customer..."
                                options={customers.map(c => ({ value: c.id, label: c.name }))}
                                value={formData.customerId}
                                onChange={(val) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        customerId: val,
                                        customerPoNumber: '',
                                        estimatedPalletCount: 0,
                                        totalWeight: 0,
                                        selectedPOs: [],
                                    }));
                                    searchPOs('', val);
                                }}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                <Search className="w-4 h-4 text-slate-400" />
                                PO Lookup
                            </label>
                            <Combobox
                                placeholder="Search PO Number..."
                                loading={poLoading}
                                options={pos
                                    .filter(p => !formData.selectedPOs.some(sp => sp.id === p.id))
                                    .map(p => ({
                                        value: p.id,
                                        label: `${p.customerPoNumber} — ${p.customerName || p.clientName || 'Unknown'}`,
                                        description: `${p.totalSkids || 0} skids • ${p.totalWeight || 0} lbs`
                                    }))}
                                value=""
                                onChange={handlePOSelect}
                                onSearch={(q) => searchPOs(q, formData.customerId)}
                                emptyText="No POs found. Type to search."
                            />
                        </div>

                        {/* Linked POs */}
                        {formData.selectedPOs.length > 0 && (
                            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                                        {formData.selectedPOs.length === 1
                                            ? `PO ${formData.selectedPOs[0].poNumber} linked`
                                            : `${formData.selectedPOs.length} POs linked`}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {formData.selectedPOs.map((po) => (
                                        <span key={po.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800/30 text-blue-700 dark:text-blue-300 rounded-md text-xs font-mono">
                                            {po.poNumber}
                                            <span className="text-slate-500 dark:text-slate-400">• {po.clientName}</span>
                                            <button type="button" onClick={() => handlePORemove(po.id)} className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors" aria-label={`Remove PO ${po.poNumber}`}>×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {formData.selectedPOs.length === 0 && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">You can schedule this now and attach a PO later.</p>
                        )}
                    </div>
                )}

                {/* Step 2: Date & Time */}
                {step === 2 && (
                    <div className="space-y-5 animate-fade-in">
                        <div className="flex items-center gap-2 mb-4">
                            <Clock className="w-5 h-5 text-blue-500" />
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Date & Time</h3>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 -mt-3">Select when the truck is expected to arrive.</p>

                        {formData.warehouseId ? (
                            <div className="space-y-3">
                                <CalendarSlotPicker
                                    warehouseId={formData.warehouseId}
                                    onSelect={handleSlotSelect}
                                />
                                {formData.startAt && (
                                    <div className="flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800/30 rounded-lg">
                                        <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0" />
                                        <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                            {(() => {
                                                const tz = user?.timeZone || 'UTC';
                                                
                                                let startDisplay = '';
                                                if (formData.startUtc) {
                                                    startDisplay = formatInWarehouseTimeZone(formData.startUtc, tz, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                                                } else if (formData.startAt) {
                                                    startDisplay = formatLocalWallClock(formData.startAt, "ccc, LLL d, h:mm a");
                                                }

                                                let endDisplay = '';
                                                if (formData.endUtc) {
                                                    endDisplay = formatInWarehouseTimeZone(formData.endUtc, tz, { hour: '2-digit', minute: '2-digit' });
                                                } else if (formData.endAt) {
                                                    endDisplay = formatLocalWallClock(formData.endAt, "h:mm a");
                                                }

                                                return `${startDisplay} — ${endDisplay}`;
                                            })()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-amber-600 dark:text-amber-400">No facility configured. Please contact your admin.</p>
                        )}
                    </div>
                )}

                {/* Step 3: Carrier Details */}
                {step === 3 && (
                    <div className="space-y-5 animate-fade-in">
                        <div className="flex items-center gap-2 mb-4">
                            <Truck className="w-5 h-5 text-blue-500" />
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Carrier Details</h3>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 -mt-3">
                            Required fields marked with <span className="text-red-500">*</span>. Select an existing carrier to auto-fill.
                        </p>

                        {/* Carrier autocomplete */}
                        {knownCarriers.length > 0 && (
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Search Existing Carriers</label>
                                <Combobox
                                    placeholder="Type carrier name or MC#..."
                                    options={knownCarriers.map((c: any) => ({
                                        value: c.id,
                                        label: c.name || c.legalName || 'Unknown',
                                        description: c.mcNumber ? `MC: ${c.mcNumber}` : undefined,
                                    }))}
                                    value=""
                                    onChange={handleCarrierSelect}
                                    emptyText="No matching carriers"
                                />
                            </div>
                        )}

                        <div className="h-px bg-slate-200 dark:bg-slate-800 my-1" />

                        {/* Required fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label htmlFor="so-carrierName" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Carrier Name <span className="text-red-500">*</span>
                                </label>
                                <input id="so-carrierName" name="carrierName" type="text" className="field w-full" value={formData.carrierName} onChange={handleChange} placeholder="Carrier Company" />
                            </div>
                            <div className="space-y-1.5">
                                <label htmlFor="so-carrierMcNumber" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    MC Number <span className="text-red-500">*</span>
                                </label>
                                <input id="so-carrierMcNumber" name="carrierMcNumber" type="text" className="field w-full" value={formData.carrierMcNumber} onChange={handleChange} placeholder="e.g. 123456" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label htmlFor="so-carrierEmail" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Carrier Email <span className="text-red-500">*</span>
                                </label>
                                <input id="so-carrierEmail" name="carrierEmail" type="email" className="field w-full" value={formData.carrierEmail} onChange={handleChange} placeholder="dispatch@carrier.com" />
                            </div>
                            <div className="space-y-1.5">
                                <label htmlFor="so-carrierPhone" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Phone <span className="text-red-500">*</span>
                                </label>
                                <input id="so-carrierPhone" name="carrierPhone" type="tel" className={`field w-full ${phoneError ? '!border-red-500 !ring-red-500/30 ring-1' : ''}`} value={formData.carrierPhone} onChange={(e) => { handleChange(e); if (phoneError) setPhoneError(''); }} placeholder="(555) 123-4567" />
                                {phoneError && <p className="text-xs text-red-500 mt-0.5">{phoneError}</p>}
                            </div>
                        </div>

                        {/* Optional fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label htmlFor="so-truckNumber" className="text-sm font-medium text-slate-700 dark:text-slate-300">Truck #</label>
                                <input id="so-truckNumber" name="truckNumber" type="text" className="field w-full" value={formData.truckNumber} onChange={handleChange} placeholder="e.g. TRK-12345" />
                            </div>
                            <div className="space-y-1.5">
                                <label htmlFor="so-trailerNumber" className="text-sm font-medium text-slate-700 dark:text-slate-300">Trailer #</label>
                                <input id="so-trailerNumber" name="trailerNumber" type="text" className="field w-full" value={formData.trailerNumber} onChange={handleChange} placeholder="e.g. TRL-67890" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label htmlFor="so-notes" className="text-sm font-medium text-slate-700 dark:text-slate-300">Notes</label>
                            <textarea id="so-notes" name="notes" className="field w-full min-h-[80px]" value={formData.notes} onChange={handleChange} placeholder="Special instructions..." />
                        </div>

                        {/* Pre-submit summary */}
                        {formData.startAt && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-600 dark:text-slate-400">
                                <p className="font-semibold text-slate-900 dark:text-slate-200 mb-1">Summary</p>
                                {/* Display using canonical UTC → warehouse timezone. Never parse wall-clock as browser Date. */}
                                <p>📅 {(() => {
                                    const tz = user?.timeZone || 'UTC';
                                    if (formData.startUtc) {
                                        return formatInWarehouseTimeZone(formData.startUtc, tz, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                                    } else if (formData.startAt) {
                                        return formatLocalWallClock(formData.startAt, "ccc, LLL d, h:mm a");
                                    }
                                    return '';
                                })()}</p>
                                {formData.customerPoNumber && <p>📦 PO: {formData.customerPoNumber}</p>}
                                {formData.carrierName && <p>🚛 {formData.carrierName}{formData.carrierMcNumber ? ` (MC: ${formData.carrierMcNumber})` : ''}</p>}
                                {formData.truckNumber && <p>🔧 Truck: {formData.truckNumber}</p>}
                                {formData.trailerNumber && <p>📎 Trailer: {formData.trailerNumber}</p>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer nav */}
            <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-900/80">
                {step > 1 ? (
                    <button
                        type="button"
                        onClick={() => setStep((step - 1) as WizardStep)}
                        className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        ← Back
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        Cancel
                    </button>
                )}

                {step < 3 ? (
                    <button
                        type="button"
                        disabled={step === 2 && !canGoToStep3}
                        onClick={() => setStep((step + 1) as WizardStep)}
                        className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next →
                    </button>
                ) : (
                    <button
                        type="button"
                        disabled={isSubmitting || !canSubmit}
                        onClick={doSubmit}
                        className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Scheduling...
                            </>
                        ) : (
                            <>
                                Schedule Appointment
                                <CheckCircle2 className="w-4 h-4" />
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
